import React, { useEffect, useState, useMemo } from 'react';
import { useDataProvider, usePermissions } from 'react-admin';
import { Area, AreaChart, XAxis, YAxis, Tooltip } from 'recharts';
import { formatNumber, getInitialChart } from '../../utils';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import useDashboardContext from '../../hooks/useDashboardContext';
import { DashboardWrapper, RotatedAxisTick } from '../../components';
import moment from 'moment';

const color = "#7869DF";

const chartMargin = { top: 20, right: 30, left: 20, bottom: 20 };
const yAxisDomain = [0, 'auto'];
const axisLine = { stroke: '#CCCCCC' };
const yAxisTick = { fill: '#333' };
const tooltipContentStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #cccccc',
    borderRadius: '5px',
    padding: '10px',
    fontSize: '14px',
    color: '#333333',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
};
const cursorStyle = { fill: '#f5f5f5' };
const activeDot = { r: 6, stroke: color, strokeWidth: 2 };
const tooltipFormatter = (value) => `${formatNumber(value)} Clientes`;

const sortData = (a, b) => {
    return new Date(a.day) - new Date(b.day);
}

export const NewCustomersChart = () => {
    const dataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const { from, to, chain } = useDashboardContext();

    const initialChart = getInitialChart(from, to, { id: 0 });
    const [chartData, setChartData] = useState(initialChart);

    const chainId = permissions && permissions.includes(PERMISSIONS.ADMIN) ? chain : getChainId();

    useEffect(() => {
        dataProvider.getList('dashboard/new-customers', {
            filter: { from, to, },
            chainId,
        }).then(({ data }) => {
            data.sort(sortData);
            data.forEach((element) => {
                element.day = moment(element.day).format('DD/MM/YYYY');
                element.id = Number(element.id);
            });
            setChartData(data);
        });
    }, [from, to, chain]);

    const chartContent = useMemo(() => (
        <AreaChart data={chartData} margin={chartMargin}>
            <YAxis
                type="number"
                domain={yAxisDomain}
                tickLine={false}
                axisLine={axisLine}
                tick={yAxisTick}
            />
            <XAxis
                dataKey="day"
                tick={<RotatedAxisTick fontSize={12} />}
                height={60}
                tickLine={false}
                axisLine={axisLine}
            />
            <Tooltip
                formatter={tooltipFormatter}
                contentStyle={tooltipContentStyle}
                cursor={cursorStyle}
            />
            <defs>
                <linearGradient id={color} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.75} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
            </defs>
            <Area
                stroke={color}
                fillOpacity={1}
                fill={`url(#${color})`}
                dataKey="id"
                name="Total"
                type="monotone"
                dot={false}
                strokeWidth={3}
                activeDot={activeDot}
                isAnimationActive={true}
            />
        </AreaChart>
    ), [chartData]);

    return (
        <DashboardWrapper
            title="Gráfico de Novos Clientes"
            permission={PERMISSIONS.NEW_CUSTOMERS_CHART}
            aspectRatio={6.0 / 2.5}
            content={chartContent}
        />
    );
};
